import { FieldViewModel, DatalayersMap, DrawingFeature, getDrawingLineDefaultDrawingStyle, getDrawingPolygonDefaultDrawingStyle, IDataLayer, IDatalayerContext, IDatalayerZoneMapInstance, IDocumentLibraryService, ImportDocument, LinePattern } from "../../omnia"
import { IDatalayerMapDependencies } from "../../types"
import { IZoneEditor } from "../types"
import { DrawBufferContext } from "./buffer/buffer"
import { ColorContext } from "./color/color"
import { getZoneDimensions } from "./dimensions"
import { EditFeature } from "./edit-feature"
import { EditLayer } from "./edit-layer"
import { Pictures } from "./picture/picture"
import { INewZoneOptions } from "./types"
import isFieldViewModel = omnia.fields.isFieldViewModel
import StyleProvider = omnia.map.OpenLayersStyleProvider

import RegularShapeFeature = omnia.map.RegularShapeFeature
import { ol_extent, ol_Feature, ol_geom, ol_GeometryType } from "@omnia/exports/openlayers"



export class GenericZoneEditor implements IZoneEditor {

    private _layer: EditLayer

    public showSelector = false

    private _clickHandler: omnia.map.ClickHandler
    private _contextMenuHandler: Function
    private _dragSelect: {
        enabled: boolean
        dispose: Function
    }

    private _colors: ColorContext
    private _fillColor: number[]

    private _zoneGeometryWatcher: Function;

    private _selection: EditFeature[]
    private _currentZone: EditFeature
    private _currentZoneChanged: boolean

    private regularShapeInteraction: omnia.map.RegularShapeEditInteraction
    private buffer: DrawBufferContext
    private _pictures: Pictures

    private _defaultMarkerColor: number[] = [0x21, 0x96, 0xf3]
    private _defaultLineColor: number[] = [0x21, 0x96, 0xf3]
    private _defaultBufferColor: any = [0xff, 0, 0, 0.75]

    public dimensions: { area: number, length: number }

    public readonly lineWidthOptions = [
        {
            value: omnia.datalayers.DefaultLineWidth,
            text: "Thin"
        },
        {
            value: omnia.datalayers.ThickLineWidth,
            text: "Thick"
        }
    ]

    public readonly linePatternOptions = [
        {
            value: LinePattern.Solid,
            text: "Solid"
        },
        {
            value: LinePattern.Dashes,
            text: "Dashes"
        },
        {
            value: LinePattern.Dots,
            text: "Dots"
        }
    ]

    constructor(
        private $scope: ng.IScope,
        private map: DatalayersMap,
        private deps: IDatalayerMapDependencies,
        private _outerGeometry: ol_geom.Geometry,
        private documentSvc: IDocumentLibraryService) {

    }

    public setup(target: IDataLayer, ctxt: IDatalayerContext) {

        const { map } = this

        if (this._layer) {
            throw new Error("Cannot be reset")
        }

        const layer = new EditLayer(target, ctxt, map.fieldsLayer)

        map.addLayer(layer, true)

        ctxt.onZoneValueChanged = zone => this.onZoneValueChanged(zone)
        this.$scope.$watch(() => getZoneDimensions(this._currentZone, this.buffer), d => this.dimensions = d, true)

        this._layer = layer

        this.setupMap()

        this._colors = new ColorContext(layer, this.map.croppingLayer)
        this.setupDragSelect()

        return layer

    }

    setLayerTargets(field: FieldViewModel, cropping: FieldViewModel, croppingList: FieldViewModel[]) {
        // Not used in this context
    }

    startEditing() {

        if (!this._clickHandler) {
            this.setupMapClickHandler()
        }

        this._colors.refreshFieldColors(true)

    }

    dispose() {

        if (this._clickHandler) {
            this._clickHandler.dispose()
        }

        if (this._contextMenuHandler) {
            this._contextMenuHandler()
            this._contextMenuHandler = null
        }

        this._dragSelect && this._dragSelect.dispose()
        this._dragSelect = undefined

        this.releaseGeometryWatcher()

        if (this.regularShapeInteraction) {
            let olmap = this.map.getMap()
            olmap.removeInteraction(this.regularShapeInteraction)
        }

        if (this.buffer) {
            this.buffer.dispose()
        }

        if (this._pictures) {
            this._pictures.dispose()
        }

        this.map.croppingLayer.styleProvider = null

    }

    private releaseGeometryWatcher() {

        if (this._zoneGeometryWatcher) {
            this._zoneGeometryWatcher()
            this._zoneGeometryWatcher = null
        }

    }


    private releaseSelection() {

        this.releaseGeometryWatcher()

        this.applyToSelection(i => i.setInactive())

        if (this.regularShapeInteraction) {
            this.regularShapeInteraction.setShape(null)
        }

        this.map.setTranslateFeatures([])

    }

    private setupMap() {

        let map = this.map
        let olmap = map.getMap()

        this.regularShapeInteraction = new omnia.map.RegularShapeEditInteraction()
        olmap.addInteraction(this.regularShapeInteraction)

        this.buffer = new DrawBufferContext(map, this.deps.topology, this._layer)

        this._pictures = new Pictures(this.$scope, this.map, this.deps)

        this._pictures.setup(this.zones)

        map.setSelectableLayers([])

        this.map.editLayer = this.layer

    }

    private setupMapClickHandler() {

        this._clickHandler = new omnia.map.ClickHandler(this.$scope, this.map, [this.map.fieldsLayer, this.layer], 10)

        this._clickHandler.checkFeatureSelection = feature => this.setCurrentZoneFromFeature(feature)
        this._clickHandler.handleDoubleClick = () => this.commitCurrent()

        this._contextMenuHandler = this.map.addTooltipProvider(p => this.handleDefaultContextMenu(p))

    }

    private setupDragSelect() {

        this._dragSelect && this._dragSelect.dispose()
        this._dragSelect = undefined

        if (this.customColors) {
            this._dragSelect = omnia.map.createDragSelectInteraction(this.map, this._layer, features => this.selectMultipleFields(features))
        }

    }

    updateZoneValues() {
        this.layer.refresh()
    }

    private handleDefaultContextMenu(tooltip: omnia.map.IMapTooltip) {

        if (tooltip.contextMenu) {

            if ((!tooltip.dblClick) && this.canAddNewFeature()) {
                tooltip.context = this;
                tooltip.template = "app/datalayers/edit/generic/create-menu.html";
                return true
            }

            tooltip.template = "-"
            return true

        }


    }

    private getImgPath(document: ImportDocument) {
        return this.documentSvc.getImgPath(document, "thumbnail");
    }

    get templateUrl(): string {
        return "app/datalayers/edit/generic/editor.html";
    }

    get valueTemplateUrl(): string {
        return null;
    }

    get zones(): EditFeature[] {
        return this.layer.zones;
    }

    get pictures() {
        return this._pictures
    }

    get colors() {
        return this._colors
    }

    isReady() {
        return true
    }

    isEditing() {
        return this.hasSelection()
    }

    isDrawing(): boolean {
        return this.map.getDrawingMode() || this.map.getEditMode()
    }

    isDrawingNew() {
        return this.map.getDrawingMode() && (!this.map.getEditMode())
    }

    isLayerEmpty(): boolean {
        return !this.zones.length
    }

    isHoldingLayer() {
        return !this.layer.fieldId
    }

    canAddNewFeature() {
        return !this.hasSelection()
    }

    hasSelection() {
        return this._currentZone || this.hasMultipleSelection()
    }

    getSelection() {
        return this._currentZone ? [this._currentZone] : (this._selection || [])
    }

    applyToSelection(action: (i: EditFeature) => void) {
        this.getSelection().forEach(f => action(f))
    }

    hasMultipleSelection() {
        return this._selection && (this._selection.length > 1)
    }

    canChangeCurrentZone() {
        return !this.isEditing()
    }

    canStop() {
        return this.isEditing()
    }

    get currentZone(): EditFeature {
        return this._currentZone
    }

    get zone(): EditFeature {
        return this._currentZone
    }

    set currentZone(value: EditFeature) {
        this.setCurrentZone(value, false);
    }

    get clipFeatures() {
        return this._layer.clipFeatures;
    }

    set clipFeatures(value: boolean) {
        this._layer.clipFeatures = value;
    }

    canClipFeatures() {
        return !!this._outerGeometry;
    }

    canEditLabel() {
        return this._currentZone && (!this._currentZone.isFieldFeature())
    }

    editLabelMultipleLines() {
        return this._currentZone && (this._currentZone.isText() || this._currentZone.isMarker())
    }

    canDeleteFeature() {
        return this._currentZone && this._currentZone.canDelete()
    }

    get layer(): EditLayer {
        return this._layer
    }

    enableCustomColors() {
        return this.isHoldingLayer()
    }

    get enableMultiSelect() {
        return this.customColors && this._dragSelect && this._dragSelect.enabled
    }

    set enableMultiSelect(value) {
        if (this.customColors && this._dragSelect) {
            this._dragSelect.enabled = value
            if (!value) {
                this.setSelection([], false)
            }
        }
    }

    get customColors() {
        return this._colors && this._colors.customFieldColors
    }

    set customColors(value) {

        this._colors.customFieldColors = value

        if ((!value) && this._currentZone && this._currentZone.isFieldFeature()) {
            this.currentZone = null
        }

        this.setupDragSelect()

    }

    get currentZoneFillColor() {
        return this._fillColor
    }

    set currentZoneFillColor(value: number[]) {

        this._fillColor = value

        let fields = false

        this.applyToSelection(zone => {

            zone.setFillColor(value, false)
            zone.setEditStyle()

            if (zone.isPoint()) {
                this._defaultMarkerColor = value
            }

            else if (zone.isLine()) {
                this._defaultLineColor = value
            }

            else if (zone.isFieldFeature) {
                fields = true
            }

            if (this.buffer.isActive()) {
                this.buffer.updateStyle()
            }

        })

        if (fields) {
            this._colors.refreshFieldColors()
        }

        this.colors.refreshColors()

    }

    showFillColorPicker() {
        const zone = this.currentZone
        if (zone && (!zone.isText())) return true
        if (this.hasMultipleSelection()) return true
        return false
    }

    showStrokeOptions() {
        const zone = this.currentZone
        return zone && (!zone.isText()) && (!zone.isFieldFeature()) && (zone.isLine() || zone.isPolygon())
    }

    showLineOptions() {
        const zone = this.currentZone
        return zone && (!zone.isText()) && zone.isLine()
    }

    hasCurrentZoneChanged() {

        if (this._currentZone) {
            if (this._currentZone.hasChanged(false)) return true;
            if (this._currentZoneChanged) return true;
        }

    }

    drawNewPolygon() {
        this.drawNewFeature("Polygon", null, getDrawingPolygonDefaultDrawingStyle(this.layer))
    }

    drawNewLine() {
        this.drawNewFeature("LineString", this._defaultLineColor, getDrawingLineDefaultDrawingStyle(this._defaultLineColor))
    }

    drawNewMargin() {

        if (!this.commitCurrent()) return

        const zone = this.createAndSelectZone(null, {
            defaultColor: this._defaultBufferColor,
            buffer: this.buffer.width
        })

        this.map.clearTooltip()

        this.startDrawingMargin(zone)

    }

    private startDrawingMargin(zone: EditFeature) {

        this.buffer.startDrawing(zone, () => {

            if (zone.feature && zone.feature.getGeometry()) {
                this._currentZoneChanged = true
            }

            this.$scope.$applyAsync()

        })

    }

    drawNewFeature(type: ol_GeometryType, defaultColor?: number[], style?: StyleProvider) {

        if (!this.commitCurrent()) return

        const zone = this.createAndSelectZone(null, { defaultColor })

        this.map.startDrawingNewFeature(this.layer, type, {

            drawStartWatcher: feature => {

                zone.setFeature(feature)

                zone.setEditStyle()

                this.watchZoneGeometry(zone)

                this._currentZoneChanged = true
                this.$scope.$applyAsync()

            },

            drawEndWatcher: () => {

                this.setSelection([this._currentZone], true)

                this._currentZoneChanged = true
                this.$scope.$applyAsync()

            },

            style

        });

    }

    addNewSquare(fromMapContextMenu: boolean) {

        this.addNewRegularShape(fromMapContextMenu, (shape, center) => {
            shape.setupSquare(center, 100, 0);
        });

    }

    addNewRectangle(fromMapContextMenu: boolean) {

        this.addNewRegularShape(fromMapContextMenu, (shape, center) => {
            shape.setupRectangle(center, 150, 100, 0);
        });

    }

    addNewCircle(fromMapContextMenu: boolean) {

        this.addNewRegularShape(fromMapContextMenu, (shape, center) => {
            shape.setupCircle(center, 100);
        });

    }

    addNewRegularShape(fromMapContextMenu: boolean, factory: (shape: RegularShapeFeature, center: number[]) => void) {

        if (!this.commitCurrent()) return

        let center = this.getNewFeatureCoord(fromMapContextMenu);

        let shape = new RegularShapeFeature();

        factory(shape, center);

        this.createNewRegularShapeZone(shape);

        this.map.clearTooltip();

    }

    addNewMarker(fromMapContextMenu: boolean) {

        if (!this.commitCurrent()) return

        let coord = this.getNewFeatureCoord(fromMapContextMenu);

        let point = new ol_geom.Point(coord);

        let feature = this.layer.createNewFeature(point, null);

        this.createAndSelectZone(feature, {
            defaultColor: this._defaultMarkerColor,
            label: "New marker",
        });

        this.map.clearTooltip();

    }

    addNewText(fromMapContextMenu: boolean) {

        if (!this.commitCurrent()) return

        const coord = this.getNewFeatureCoord(fromMapContextMenu)

        const text = this.layer.createNewText(coord)

        this.setSelection(text, true)

        this.map.clearTooltip()

    }

    addNewPicture(fromMapContextMenu: boolean) {

        if (!this.commitCurrent()) return

        this.addNewMarker(fromMapContextMenu)
        this._pictures.selectPicture(this.currentZone)

    }

    private getNewFeatureCoord(fromMapContextMenu: boolean): number[] {

        if (fromMapContextMenu) {
            let t = this.map.tooltip;
            if (t && t.mapPosition) {
                return t.mapPosition;
            }
        }

        let e = this.map.getCurrentViewExtent();
        return ol_extent.getCenter(e);

    }

    canSave(): boolean {
        if (!this.canCommitCurrent()) return false
        if (this.zones.length) return true
        return this.layer.hasVisibleFieldFeatures()
    }

    canCommitCurrent(): boolean {

        if (this.buffer.isActive()) {
            return this.buffer.canCommit()
        }

        if (this.map.getDrawingMode()) {
            return this.map.canCommit()
        }

        return true

    }

    cancelCurrent() {

        const shouldDelete = this.isDrawingNew()
        const zone = this._currentZone

        this.buffer.cancelEdit()
        this.map.cancelEdit()
        this.map.setTranslateFeatures([])

        let fields = false

        this.applyToSelection(z => {
            z.cancelChanges()
            fields = fields || z.isFieldFeature()
        })

        if (zone) {

            if (shouldDelete) {
                this.deleteCurrentZone()
            }
            else {
                this._pictures.setPictureOverlay(zone)
            }

        }

        if (fields) {
            this._colors.refreshFieldColors()
        }

        this.setCurrentZone(null, false)

    }

    commitCurrent(): boolean {

        if (!this.canCommitCurrent()) return false

        this.buffer.commitEdit()
        this.map.commitEdit()
        this.map.setTranslateFeatures([])

        const zone = this.currentZone

        if (zone) {

            if (this.clipFeatures && zone.canClipToBoundary()) {
                zone.clipToBoundary(this.deps.topology, this._outerGeometry);
            }

            zone.commitState()

        }


        this.setSelection(null, true)

        return true

    }


    private setCurrentZoneFromFeature(feature: ol_Feature) {

        let zone = this.zones.find(z => z.feature == feature)
        let field: EditFeature = undefined

        if ((!zone) && this.customColors) {

            const data = feature.get("data")

            if (isFieldViewModel(data) && data.isField()) {
                field = zone = this.layer.getFieldFeature(data, feature, true)
            }

        }

        if (field && this.enableMultiSelect) {

            let selection = (this._selection || [this._currentZone]).filter(z => !!z)

            if (selection.every(s => s.isFieldFeature())) {

                if (!selection.remove(s => s == field)) {
                    selection.push(field)
                }

                this.setSelection(selection, true)
                return true

            }

        }

        if (zone && (zone != this._currentZone)) {
            this.setCurrentZone(zone, true)
            return true
        }

    }

    setCurrentZone(zone: EditFeature, mapSelection: boolean = false) {

        if (this.commitCurrent()) {

            this.setSelection([zone], mapSelection)

            return true

        }

    }

    selectMultipleFields(features: ol_Feature[]) {

        const fields = features
            .map(f => f.get("data"))
            .filter(i => i instanceof EditFeature)
            .map(i => i as EditFeature)
            .filter(i => i.isFieldFeature())

        this.setSelection(fields, true)

    }

    setSelection(selection: EditFeature[], mapSelection: boolean) {

        this.releaseSelection()

        selection = selection || []

        const zone = selection.length == 1 ? selection[0] : null

        this._currentZone = zone
        this._currentZoneChanged = false
        this._selection = selection.length > 1 ? selection : null

        this.map.setTranslateFeatures([])

        this._fillColor = undefined

        if (selection.length > 1) {

            selection.forEach(s => {
                s.setActive()
                s.setEditStyle()
            })

        }

        if (zone) {

            zone.setActive()

            if (zone.isBuffer()) {
                this.startDrawingMargin(zone)
            }

            else if (zone.isRegularShape()) {
                zone.shape.updateGeometry()
                this.regularShapeInteraction.setShape(zone.shape)
            }

            else if (zone.isText()) {
                this.map.setTranslateFeatures([zone.feature])
                this.regularShapeInteraction.setFeature(zone.feature)
            }

            else if (zone.isMarker()) {
                this.map.setTranslateFeatures([zone.feature])
            }

            else if (zone.isLine()) {
                this.map.startEditFeatureWithData(zone, false)
            }

            else if (zone.isFieldFeature()) {
                // NOP
            }

            else {
                this.map.startEditFeatureWithData(zone, false)
                this.map.setTranslateFeatures([])
                this.regularShapeInteraction.setFeature(zone.feature)
            }

            this._fillColor = zone.fillColor

            zone.setEditStyle()

        }

        this._pictures.setCurrentZone(zone)

        if (mapSelection) {
            this.$scope.$applyAsync()
        }

        this.watchZoneGeometry(zone)

    }


    private watchZoneGeometry(zone: DrawingFeature) {

        if (!zone) return
        if (!zone.feature) return

        if (this._zoneGeometryWatcher) {
            this._zoneGeometryWatcher()
        }

        this._zoneGeometryWatcher = omnia.map.observeOpenlayersObject(zone.feature, "change", () => {

            this._pictures.updatePictureOverlayPosition(zone)

            if (zone.picture && zone.picture.ExifLocation) {
                zone.setUsePictureCoordinates()
            }

            if (!this._currentZoneChanged) {
                this._currentZoneChanged = zone.hasGeometryChanged()
            }

            zone.resetDimensions()

            this.$scope.$applyAsync()

        });

    }

    setCurrentZoneEditStyle() {
        if (this._currentZone) {
            this._currentZone.setEditStyle()
        }
    }

    onZoneValueChanged(zone: IDatalayerZoneMapInstance) {
        this._pictures.updatePictureOverlayPosition(zone)
    }

    private deleteCurrentZone() {

        const zone = this._currentZone

        this.cancelCurrent()

        this._pictures.removePictureOverlay()

        this.layer.removeZone(zone)

    }

    private createAndSelectZone(feature: ol_Feature, options?: INewZoneOptions) {

        const zone = this.layer.createZone(feature, options)

        this.setSelection([zone], true)

        return zone

    }

    private createNewRegularShapeZone(shape: RegularShapeFeature) {

        const zone = <EditFeature>this.layer.createNewZone()

        const feature = this.layer.createNewFeature(null, zone)
        shape.feature = feature

        zone.setShape(shape)

        zone.commitState()

        this.setSelection([zone], true)

    }

    validateLayer() {
        return true
    }

    viewExtent(zone: EditFeature) {
        this.map.viewExtentForDataItem(zone)
    }

}
